import React from 'react';
import {useTheme} from '@mui/material/styles';
import {stationDisplayName, stationNumber} from '@embertracking/common';

import {attachmentSelector, useProtectedFileUrl} from '../data/protectedFiles.js';

import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';

// Shared so the hub renders a member exactly like the department app does. The
// The authenticated file client is injected. Omit it on a surface that lacks
// permission to the member's attachment and the avatar falls back to initials.

const stringToColor = string => {
    if (!string) {
        return;
    }

    let hash = 0;
    let i;

    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';
    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
};

export default function UserAvatar(props) {
    const {user, fileClient, recordPath, sx, ...rest} = props;

    const theme = useTheme();
    const {uid, image, firstName = '', lastName = '', fullName = '', isStation} = user || {};
    // The avatar rendition is ~15 KB against the thumbnail's ~300 KB. Images
    // uploaded before it existed (and not yet backfilled) fall back.
    const variant = image?.avatarPath ? 'avatar' : image?.thumbnailPath ? 'thumbnail' : undefined;
    const {url: imageUrl} = useProtectedFileUrl(fileClient, attachmentSelector(image, recordPath, variant));

    if (!uid) {
        return '-';
    }

    let displayText = `${firstName[0]}${lastName[0]}`;
    if (!firstName || !lastName) {
        displayText = '';
    }

    let bgcolor = stringToColor(fullName);
    // A station's `name` is free text a department typed — "201", but also
    // "Station 1" or an address (see @embertracking/common/stations). Dropping
    // it in raw overflowed the circle ("Station 1" rendered as "tatio"), so show
    // the number alone. An address-only station has none; it falls through to
    // the same empty circle a member with no first/last name gets.
    let label = fullName;
    if (isStation) {
        displayText = stationNumber(user);
        label = stationDisplayName(user);
        bgcolor = theme.palette.primary.main;
    }

    return (
        <Tooltip title={label}>
            <Avatar
                src={imageUrl}
                alt={label}
                sx={{
                    color: '#fff !important',
                    bgcolor,
                    ...sx
                }}
                {...rest}
            >
                {displayText}
            </Avatar>
        </Tooltip>
    );
}
